import { cn } from '@/lib/utils';
import { FileText, History, type LucideIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { sideViewState } from '@chainlit/react-client';

import InternetGlobeIcon from '@/components/icons/InternetGlobeIcon';
import { Button } from '@/components/ui/button';
import { ResizableHandle, ResizablePanel } from '@/components/ui/resizable';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatCitationRefLabel, getCitationKind } from '@/lib/message';

import { Element } from './Elements';

const citationIcon: Record<'document' | 'memory' | 'web', LucideIcon> = {
  document: FileText,
  memory: History,
  web: InternetGlobeIcon
};

export default function ElementSideView() {
  const [sideView, setSideView] = useRecoilState(sideViewState);
  const isMobile = useIsMobile();
  const { i18n } = useTranslation();

  if (!sideView) return null;

  const isCanvas = sideView.title === 'canvas';
  const kind = getCitationKind(sideView.title);
  const english = i18n.resolvedLanguage?.toLowerCase().startsWith('en');
  const title = kind
    ? formatCitationRefLabel(sideView.title, english ? 'en-US' : 'sr-RS')
    : sideView.title;
  const closeLabel = english ? 'Close side panel' : 'Zatvori bočni panel';
  const CitationIcon = kind ? citationIcon[kind] : undefined;

  const content = (
    <div
      id="side-view-content"
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]',
        isCanvas ? 'p-0' : 'px-4 py-4'
      )}
    >
      <div className={cn('flex flex-col', isCanvas ? 'h-full' : 'gap-5')}>
        {sideView.elements.map((element) => (
          <div
            key={element.id}
            className={cn(
              !isCanvas &&
                'min-w-0 break-words text-sm leading-6 text-foreground/85 [overflow-wrap:anywhere] [&_.side-text]:text-sm [&_.side-text]:leading-6'
            )}
          >
            <Element element={element} />
          </div>
        ))}
      </div>
    </div>
  );

  const header = !isCanvas ? (
    <div className="flex shrink-0 items-center gap-3 border-b border-border/40 px-4 py-4">
      {CitationIcon ? (
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-foreground/[0.06] text-foreground/75">
          <CitationIcon aria-hidden="true" size={17} strokeWidth={1.8} />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div
          id="side-view-title"
          className="truncate text-base font-medium leading-tight text-foreground"
        >
          {title}
        </div>
      </div>
      <Button
        aria-label={closeLabel}
        className="h-9 w-9 shrink-0 rounded-full"
        onClick={() => setSideView(undefined)}
        size="icon"
        title={closeLabel}
        variant="ghost"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  ) : null;

  if (isMobile) {
    return (
      <Sheet open onOpenChange={(open) => !open && setSideView(undefined)}>
        <SheetContent
          aria-describedby={undefined}
          className={cn(
            'flex h-full w-[92vw] max-w-[430px] flex-col gap-0 overflow-hidden border-l border-border/50 bg-background/90 p-0 backdrop-blur-xl md:hidden',
            isCanvas
              ? 'w-full max-w-none'
              : '[&>button.absolute]:hidden'
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{isCanvas ? 'Canvas' : title}</SheetTitle>
          </SheetHeader>
          {header}
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <ResizableHandle className="hidden bg-border/35 md:block" />
      <ResizablePanel
        minSize={isCanvas ? 35 : 18}
        defaultSize={isCanvas ? 50 : 18}
        maxSize={isCanvas ? 70 : 28}
        className="hidden min-h-0 overflow-hidden md:flex"
      >
        <aside
          aria-labelledby={isCanvas ? undefined : 'side-view-title'}
          className="relative flex min-h-0 w-full flex-col overflow-hidden border-l border-border/40 bg-transparent"
        >
          {isCanvas ? (
            <Button
              aria-label={closeLabel}
              className="absolute left-3 top-3 z-20 h-9 w-9 rounded-full bg-background/85 shadow-sm backdrop-blur"
              onClick={() => setSideView(undefined)}
              size="icon"
              title={closeLabel}
              variant="outline"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : (
            header
          )}
          {content}
        </aside>
      </ResizablePanel>
    </>
  );
}
